import * as React from 'react';
import { Column } from 'react-table';
import { Overflow, tableColumns } from '@core/table';
import uniqBy from 'lodash/uniqBy';
import * as Icons from '@ant-design/icons';

import { IHandoverQueue } from '../../interfaces';
import { Button, Dropdown, Menu, message, Select } from 'antd';
import { StopPropagation } from '@core/ui';
import { IGetHandoverQueueRepo, GetHandoverQueueRepoType } from '../../repos';
import { useService } from '@core/inversify-react';
import { printHandoverCheck } from '@modules/warehouse';
import { useAuth } from '@modules/auth';

export const useHandoverQueueTableColumns = () => {
  const { user } = useAuth();
  const getHandoverQueueRepo = useService<IGetHandoverQueueRepo>(GetHandoverQueueRepoType);

  const handlePrintHandoverCheck = React.useCallback(
    async (handoverTaskId: number | string) => {
      message.loading({ content: 'Yüklənir...', key: 'loading' });
      const result = await getHandoverQueueRepo.execute({ id: handoverTaskId });
      message.destroy('loading');

      if (result.status === 200) {
        await printHandoverCheck(user.data, handoverTaskId, result.response);
      } else {
        message.error(result.response);
      }
    },
    [getHandoverQueueRepo, user.data],
  );

  const actionsColumn = React.useMemo<Column<IHandoverQueue>>(
    () => ({
      ...tableColumns.actions,
      Cell: ({ row: { original } }) => {
        const [visible, setVisible] = React.useState(false);
        const [loading, setLoading] = React.useState(false);

        const onPrint = async () => {
          setLoading(true);
          await handlePrintHandoverCheck(original.id);
          setLoading(false);
        };

        const overlay = (
          <Menu onClick={() => setVisible(false)}>
            <Menu.Item disabled={loading} icon={<Icons.PrinterOutlined spin={loading} />} onClick={onPrint}>
              Çap et
            </Menu.Item>
          </Menu>
        );

        return (
          <StopPropagation>
            <Dropdown visible={visible} onVisibleChange={setVisible} overlay={overlay}>
              <Button icon={<Icons.MoreOutlined />} size='small' />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [handlePrintHandoverCheck],
  );

  return React.useMemo<Column<IHandoverQueue>[]>(
    () => [
      actionsColumn,
      {
        ...tableColumns.small,
        accessor: (row) => row.id,
        id: 'id',
        Header: 'Kod',
      },
      {
        ...tableColumns.small,
        accessor: (row) => row.user.id,
        id: 'user_id',
        Header: 'M. kodu',
      },
      {
        accessor: (row) => row.user.fullName,
        id: 'user_name',
        Header: 'M. adı',
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.declarations,
        Header: 'Bağlamalar',
        filterable: false,
        sortable: false,
        Cell: ({ cell: { value } }) => `${value.length} bağlama`,
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.declarations,
        Header: 'Yeşiklər',
        filterable: false,
        sortable: false,
        Cell: ({ row: { original } }) => {
          const boxes = uniqBy<{ id: number; name: string }>(
            original.declarations.map((declaration) => declaration.box).filter((box) => !!box),
            (box) => box.id,
          );

          return <Overflow>{boxes.map((box) => box.name).join(', ')}</Overflow>;
        },
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.status.id,
        id: 'state_id',
        Header: 'Status',
        Filter: ({ column: { setFilter, filterValue } }) => (
          <Select style={{ width: '100%' }} allowClear={true} onChange={setFilter} value={filterValue}>
            <Select.Option value='41'>Gözləmədə</Select.Option>
            <Select.Option value='42'>İcra edilir</Select.Option>
            <Select.Option value='43'>Təhvil verilib</Select.Option>
          </Select>
        ),
        Cell: ({ row: { original } }) => original.status.name,
      },
      {
        accessor: (row) => row.cashier.fullName,
        id: 'cashier_name',
        Header: 'İcraçı',
      },
      {
        ...tableColumns.date,
        accessor: (row) => row.createdAt,
        id: 'created_at',
        Header: 'Yaradılma tarixi',
      },
    ],
    [actionsColumn],
  );
};
